import { RefObject, useEffect, useState } from "react";

import PropTypes from "prop-types";

import { useAnalyticsTracker } from "components/instrumentation/source/analytics";
import { useSearchContext } from "components/store/source";
import {
  hideSearchByPhraseDropdown,
  setGeoBounds,
  setGeoDistance,
  setNeighborhood,
  setSearchByPhraseDropdownRef,
  setSortBy,
  showSearchByPhraseDropdown,
} from "components/store/source/actions/searchCriteriaActions";
import { useMedia } from "components/ui/source";

import { IconPlacemarker } from "icons";

import { DestinationType, SearchByPhrase } from "./SearchByPhrase";

export type SearchByPhraseContainerProps = {
  buildingName: string;
  neighborhoodData: {
    neighborhoodFilter: {
      options: DestinationType[];
      selected: DestinationType;
    };
  };
  v2?: boolean;
};

export function SearchByPhraseContainer({
  neighborhoodData,
  buildingName,
  v2,
}: SearchByPhraseContainerProps) {
  // TODO: need to define default distance somewhere??
  const getValue = item =>
    item.formattedSuggestion
      ? { center: { ...item.geoCoordinates }, distance: "50mi" }
      : item.value;

  const getLabel = item =>
    item.formattedSuggestion ? item.formattedSuggestion.mainText : item.label;

  const getIcon = item =>
    item.formattedSuggestion ? <IconPlacemarker size="md" /> : null;

  const cleanLabel = label => (label === "All Neighborhoods" ? "" : label);

  const setTrimmedPhrase = newPhrase => {
    const trimmedPhrase = newPhrase ? newPhrase.trimStart() : newPhrase;
    setPhrase(trimmedPhrase);
  };

  const { analytics } = useAnalyticsTracker();
  const { criteria, dispatch } = useSearchContext();
  const {
    destination,
    neighborhood,
    searchByPhraseDropdownRef,
    searchByPhraseDropdownOpen,
    mapInstance,
  } = criteria;
  const {
    neighborhoodFilter: { options: neighborhoods, selected },
  } = neighborhoodData;
  const [phrase, setPhrase] = useState(
    buildingName ||
      (selected && selected[0] && cleanLabel(getLabel(selected[0]))),
  );
  const [inputRef, setInputRef] = useState<RefObject<HTMLInputElement>>(
    searchByPhraseDropdownRef,
  );
  const isMobile = useMedia(["$tablet-lg", "$mobile-lg"], [true, true], false);

  const track = location => {
    analytics.track("Search By Address Entered", {
      city: destination,
      location,
    });
  };

  useEffect(() => {
    // Set ref of dropdown so it can be closed on blur
    if (!inputRef) return;
    dispatch(setSearchByPhraseDropdownRef(inputRef));
  }, [inputRef, dispatch]);

  const neighborhoodSelected = selection => {
    if (!selection) return false;
    return !selection.geoCoordinates;
  };

  const phraseInNeighborhoodList = () => {
    return neighborhoods
      .map(neighborhood => neighborhood.label)
      .includes(phrase);
  };

  const mapBounds = () => {
    if (!mapInstance) return null;

    return mapInstance.getBounds();
  };

  const mapCenter = () => {
    if (!mapInstance) return { lat: 0, lng: 0 };

    const { lat, lng } = mapInstance.getCenter();

    if (typeof lat === "function") {
      return {
        lat: lat() || 0,
        lng: lng() || 0,
      };
    } else {
      return {
        lat: lat || 0,
        lng: lng || 0,
      };
    }

  };

  const showDropdown = (callback = () => {}) => {
    if (!searchByPhraseDropdownOpen) {
      dispatch(showSearchByPhraseDropdown());
      callback();
    }
  };

  const hideDropdown = () => dispatch(hideSearchByPhraseDropdown());

  const handleBlur = () => {
    hideDropdown();
    if (!phrase) {
      const selectedPhrase =
        selected && selected[0] && cleanLabel(getLabel(selected[0]));
      setTrimmedPhrase(selectedPhrase);
    }
  };

  const handleFocus = () => {
    showDropdown();
    if (phraseInNeighborhoodList()) {
      setTrimmedPhrase("");
    }
  };

  const handleChange = value => {
    setTrimmedPhrase(value);
    showDropdown();
  };

  const handleSelectedChange = selection => {
    if (!selection) return;

    const label = getLabel(selection);
    const value = getValue(selection);

    if (neighborhoodSelected(selection)) {
      dispatch(setNeighborhood(value));
      dispatch(setGeoBounds(null));
      dispatch(setGeoDistance(null));
    } else {
      dispatch(setNeighborhood("all_neighborhoods"));
      dispatch(setGeoDistance(value));
      dispatch(setSortBy("distance_to_address_asc"));
    }

    setTrimmedPhrase(label);
    hideDropdown();
    track(label);
  };

  const handleClear = () => {
    dispatch(setGeoBounds(null));
    dispatch(setGeoDistance(null));
  };

  const handleClearNeighborhood = () => {
    setTrimmedPhrase("");
    dispatch(setNeighborhood("all_neighborhoods"));
  };

  const items = !phrase || phraseInNeighborhoodList() ? neighborhoods : [];

  return (
    <SearchByPhrase
      places={items}
      selected={
        // we're only supporting one selected neighborhood at the moment
        selected
          ? selected[0]
          : {
              label: neighborhood,
              value: neighborhood,
            }
      }
      buildingName={buildingName}
      isOpen={searchByPhraseDropdownOpen}
      isMobile={isMobile}
      inputValue={phrase}
      getValue={getValue}
      getLabel={getLabel}
      getIcon={getIcon}
      onClick={handleFocus}
      onFocus={handleFocus}
      onChange={handleChange}
      onSelectedChange={handleSelectedChange}
      onBlur={handleBlur}
      onClear={handleClear}
      onClearNeighborhood={handleClearNeighborhood}
      setInputRef={setInputRef}
      mapCenter={mapCenter()}
      mapBounds={mapBounds()}
      v2={v2}
    />
  );
}

SearchByPhraseContainer.propTypes = {
  neighborhoodData: PropTypes.object,
  buildingName: PropTypes.string,
  v2: PropTypes.bool,
};

SearchByPhraseContainer.defaultProps = {
  neighborhoodData: null,
  buildingName: null,
  v2: false,
};
